<template>
    <MainModal 
        :show-modal="show" 
        title="Upgrade account plan" 
        size="sm"
        @close="closeBillingModal"
    >
        <template #body>
            <div class="form__item mb--0">
                <FormLabel title="Choose a plan" />
                <CustomDropdown 
                    id="planDropdown" 
                    v-model="selectedPlan" 
                    :options="planOptions"
                    btn-text-transform="capitalize"
                    custom-text
                    shadow
                    :searchable="false"
                    list-type="plans" 
                    @select="selectPlan" 
                >
                    <template #text="{ item }">
                        <div class="flex align-items-center justify-content-between w-100" :class="{ 'disabled': item === 'FREE' }">
                            <span class="dropdown--item-label">
                                {{ item }}/{{ $country.getSymbol(subscriptionInfo.currency, 'currency') }} {{ getPlanInfo(item) }}
                            </span>
                            <span v-if="isCurrentPlan(item)" class="tag--pink text--xs tag bold badge rounded-pill">
                                Current plan
                            </span>
                        </div>
                    </template>
                </CustomDropdown>
            </div>
        </template>

        <template #footer>
            <div class="w-100 flex justify-content-end align-items-center">
                <BaseButton 
                    class="mr--5" 
                    label="Cancel" 
                    variant="outline" 
                    :disabled="btnLoading" 
                    @submit="closeBillingModal"
                />
                <BaseButton 
                    type="submit" 
                    label="Continue"
                    :loading="btnLoading" 
                    :disabled="btnDisabled" 
                    @submit="handleSubmit"
                />
            </div>
        </template>
    </MainModal>
</template>

<script>
import MainModal from "./MainModal.vue";
import BaseButton from "../buttons/BaseButton.vue";
import FormLabel from "../forms/FormLabel.vue";
import CustomDropdown from "../dropdowns/CustomDropdown.vue";
import { convertNumStringToNum } from "@/utils/numbers";

export default {
    name: "UpgradeBillingPlanModal",

    components: {
        BaseButton,
        FormLabel,
        MainModal,
        CustomDropdown,
    },

    props: {
        modalType: {
            type: String,
            default: "add",
        },

        show: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["cancel", "refresh-payment-list"],

    data() {
        return {
            selectedPlan: "",
            btnLoading: false,
            showExistingCards: false,
        };
    },

    computed: {
        savedCards() {
            return this.$store.getters["subscription/savedCards"];
        },

        accountPlan() {
            return this.subscriptionInfo?.plan || "free";
        },

        subscriptionInfo() {
            return this.$store.getters["subscription/subscriptionInfo"];
        },

        systemPlans() {
            return this.$store.getters["config/systemPlans"];
        },

        planOptions() {
            return Object.keys(this.systemPlans).map(p => p.toUpperCase());
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },

        showCardOptionCheck() {
            if (this.selectedPlan === "FREE") {
                return false;
            }

            if (this.savedCards.filter(x => x.status === "active" && x.isDefault).length) {
                return true;
            }  

            return false;
        },

        btnDisabled() {
            if (!this.selectedPlan) {
                return true;
            }

            return false;
        },
    },

    mounted() {
        this.selectedPlan = this.accountPlan;
    },

    methods: {
        selectPlan(val) {
            if (val?.toLowerCase() === this.accountPlan?.toLowerCase()) return;

            if (val === "FREE") return;

            this.selectedPlan = val;
        },

        getPlanInfo(plan) {
            return this.$format_amount(this.systemPlans[plan?.toLowerCase()]?.amount[this.subscriptionInfo?.currency]?.monthlyAmount, 2) || 0;
        },

        isCurrentPlan(plan) {                        
            return plan?.toLowerCase() === this.accountPlan?.toLowerCase();
        },

        closeBillingModal() {
            this.selectedPlan = "";
            this.showExistingCards = false;
            this.$emit("cancel");
        },

        async handleSubmit() {
            try {
                this.btnLoading = true;
                const amount = this.getPlanInfo(this.selectedPlan);
                
                if (!this.selectedPlan) {
                    this.$toast.error("Please select a plan");
                    return;
                }  
                
                if (this.selectedPlan === this.accountPlan) {
                    this.$toast.error("Please select a different plan");
                    return;
                }  

                const payload = {
                    email: this.profile.email,
                    targetPlan: this.selectedPlan,
                    amount: convertNumStringToNum(amount),
                    metadata: JSON.stringify({
                        cancel_action: `${window.location.origin}/dashboard/settings/payments-and-billings`,
                    }),
                    callback_url: `${window.location.origin}/payments/verify`,
                };

                const data = await this.$store.dispatch("subscription/updateSubscriptionPlan", payload);

                if (data?.url) {
                    sessionStorage.setItem("z:t_p", this.selectedPlan);
                    sessionStorage.setItem("z:t_type", "dcdahsygh");

                    this.closeBillingModal();

                    window.location.href = data.url;
                    return;
                } else {
                    this.$store.dispatch("subscription/setSubscriptionInfo", { ...data.subscription.status });
                }

                // if (!this.selectedPlan !== "FREE") {
                //     this.$emit("refresh-payment-list", true);
                // }
                this.closeBillingModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.btnLoading = false;
            }
        },
    },
};
</script>